<template>
    <v-card class="elevation-1 rounded-4">
        <v-form @submit.prevent="saveCompanyAnnouncement" ref="form">
            <v-toolbar flat height="45px">
                <span class="font-weight-bold">{{mode}} Company Announcements</span>
                <v-spacer></v-spacer>
                <v-btn icon @click="closeDialog">
                    <v-icon>close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-card-text>

                <!-- title -->
                <v-layout row wrap>
                    <v-flex xs12 class="form-data">
                        <v-text-field label="Title"
                            v-model="title"
                            :rules="[rules.required]"
                            maxlength="75"
                            hint="Max 75 alphanumeric characters"
                            :disabled="disabled">
                        </v-text-field>
                    </v-flex>
                </v-layout>

                <!-- category and types -->
                <v-layout row wrap>
                    <v-flex xs12 sm6 class="form-data">
                        <v-autocomplete
                            v-model="categoryId"
                            :disabled="disabled"
                            :items="categories"
                            :rules="[rules.required]"
                            chips
                            color="primary lighten-2"
                            label="Category"
                            item-text="name"
                            item-value="categoryId"
                            >
                            <template v-slot:selection="data">
                                <v-chip :selected="data.selected" dark :color="getChipColor()" class="chip--select-multi ml-0">
                                    {{ data.item.name }}
                                </v-chip>
                            </template>
                            <template v-slot:item="data">
                                <template v-if="typeof data.item !== 'object'">
                                    <v-list-tile-content v-text="data.item"></v-list-tile-content>
                                </template>
                                <template v-else>
                                    <v-list-tile-content>
                                        <v-list-tile-title v-html="data.item.name"></v-list-tile-title>
                                        <v-list-tile-sub-title v-html="data.item.group"></v-list-tile-sub-title>
                                    </v-list-tile-content>
                                </template>
                            </template>
                        </v-autocomplete>
                    </v-flex>
                    <v-flex xs12 sm6 class="form-data">
                        <v-autocomplete
                            v-model="categoryTypeId"
                            :disabled="disabled||!categoryId"
                            :items="categoryTypes"
                            :rules="[rules.required]"
                            chips
                            color="primary lighten-2"
                            label="Type"
                            item-text="name"
                            item-value="categoryTypeId"
                            >
                            <template v-slot:selection="data">
                                <v-chip :selected="data.selected" class="chip--select-multi ml-0">
                                    {{ data.item.name }}
                                </v-chip>
                            </template>
                            <template v-slot:item="data">
                                <template v-if="typeof data.item !== 'object'">
                                    <v-list-tile-content v-text="data.item"></v-list-tile-content>
                                </template>
                                <template v-else>
                                    <v-list-tile-content>
                                        <v-list-tile-title v-html="data.item.name"></v-list-tile-title>
                                        <v-list-tile-sub-title v-html="data.item.group"></v-list-tile-sub-title>
                                    </v-list-tile-content>
                                </template>
                            </template>
                        </v-autocomplete>
                    </v-flex>
                </v-layout>

                <!-- effective and expiration dates -->
                <v-layout row wrap>
                    <v-flex xs12 sm6 class="form-data">
                        <v-layout row wrap>
                            <v-flex xs12 sm6 :class="isMobile?'':'pr-10'">
                                <v-menu v-model="menuEffectiveDateDay" :close-on-content-click="false" :nudge-right="40"
                                    lazy transition="scale-transition" offset-y full-width min-width="290px">
                                    <template v-slot:activator="{ on }">
                                        <v-text-field v-model="effectiveDateDay" label="Event Start Date" readonly v-on="on" :rules="[rules.required]" :disabled="disabled" hint="Event start date"></v-text-field>
                                    </template>
                                    <v-date-picker no-title v-model="effectiveDateDay" :allowed-dates="allowedDatesForEffectiveDate" @input="onEffectiveDateInput"></v-date-picker>
                                </v-menu>
                            </v-flex>
                            <v-flex xs12 sm6 :class="isMobile?'':'pl-10'">
                                <v-menu
                                    ref="menuEffectiveDateTime"
                                    v-model="menuEffectiveDateTime"
                                    :close-on-content-click="false"
                                    :return-value.sync="effectiveDateTime"
                                    lazy
                                    transition="scale-transition"
                                    offset-y
                                >
                                    <template v-slot:activator="{ on }">
                                    <v-text-field
                                        label="Event Start Time"
                                        v-model="effectiveDateTime"
                                        readonly
                                        v-on="on"
                                        :rules="[rules.required]"
                                    ></v-text-field>
                                    </template>
                                    <v-time-picker
                                        v-if="menuEffectiveDateTime"
                                        v-model="effectiveDateTime"
                                        @click:minute="$refs.menuEffectiveDateTime.save(effectiveDateTime)"
                                        full-width
                                        scrollable
                                        format="ampm"
                                    ></v-time-picker>
                                </v-menu>
                            </v-flex>
                        </v-layout>
                    </v-flex>
                    <v-flex xs12 sm6 class="form-data">
                         <v-layout row wrap>
                            <v-flex xs12 sm6 :class="isMobile?'':'pr-10'">
                                <v-menu v-model="menuExpirationDateDay" :close-on-content-click="false" :nudge-right="40"
                                    lazy transition="scale-transition" offset-y full-width min-width="290px">
                                    <template v-slot:activator="{ on }">
                                        <v-text-field v-model="expirationDateDay" label="Event End Date" readonly v-on="on" :rules="[rules.required]" :disabled="disabled||!effectiveDateDay" hint="Event end date"></v-text-field>
                                    </template>
                                    <v-date-picker no-title v-model="expirationDateDay" :allowed-dates="allowedDatesForExpirationDate" @input="menuExpirationDateDay = false"></v-date-picker>
                                </v-menu>
                            </v-flex>
                            <v-flex xs12 sm6 :class="isMobile?'':'pl-10'">
                                <v-menu
                                    ref="menuExpirationDateTime"
                                    v-model="menuExpirationDateTime"
                                    :close-on-content-click="false"
                                    :return-value.sync="expirationDateTime"
                                    lazy
                                    transition="scale-transition"
                                    offset-y
                                >
                                    <template v-slot:activator="{ on }">
                                    <v-text-field
                                        label="Event End Time"
                                        v-model="expirationDateTime"
                                        :disabled="disabled || !expirationDateDay"
                                        readonly
                                        v-on="on"
                                        :rules="[rules.required]"
                                    ></v-text-field>
                                    </template>
                                    <v-time-picker
                                        v-if="menuExpirationDateTime"
                                        v-model="expirationDateTime"
                                        :allowed-hours="allowedExpirationHours"
                                        :allowed-minutes="allowedExpirationMinutes"
                                        full-width
                                        @click:minute="$refs.menuExpirationDateTime.save(expirationDateTime)"
                                        scrollable
                                        format="ampm"
                                    ></v-time-picker>
                                </v-menu>
                            </v-flex>
                        </v-layout>
                    </v-flex>
                </v-layout>

                <!-- display start and end date -->
                <v-layout row wrap>
                    <v-flex xs12 sm6 class="form-data">
                        <v-layout row wrap>
                            <v-flex xs12 sm6 :class="isMobile?'':'pr-10'">
                                <v-menu v-model="menuDisplayStartDateDay" :close-on-content-click="false" :nudge-right="40"
                                    lazy transition="scale-transition" offset-y full-width min-width="290px">
                                    <template v-slot:activator="{ on }">
                                        <v-text-field v-model="displayStartDateDay" label="Display Start Date" readonly v-on="on" :rules="[rules.required]" :disabled="disabled||!expirationDateDay" hint="When the announcement becomes visible"></v-text-field>
                                    </template>
                                    <v-date-picker no-title v-model="displayStartDateDay" :allowed-dates="allowedDatesForDisplayStartDate" @input="menuDisplayStartDateDay = false"></v-date-picker>
                                </v-menu>
                            </v-flex>
                            <v-flex xs12 sm6 :class="isMobile?'':'pl-10'">
                                <v-menu
                                    ref="menuDisplayStartDateTime"
                                    v-model="menuDisplayStartDateTime"
                                    :close-on-content-click="false"
                                    :return-value.sync="displayStartDateTime"
                                    lazy
                                    transition="scale-transition"
                                    offset-y
                                >
                                    <template v-slot:activator="{ on }">
                                    <v-text-field
                                        label="Display Start Time"
                                        v-model="displayStartDateTime"
                                        :disabled="disabled||!displayStartDateDay"
                                        readonly
                                        v-on="on"
                                        :rules="[rules.required]"
                                    ></v-text-field>
                                    </template>
                                    <v-time-picker
                                        v-if="menuDisplayStartDateTime"
                                        v-model="displayStartDateTime"
                                        @click:minute="$refs.menuDisplayStartDateTime.save(displayStartDateTime)"
                                        full-width
                                        scrollable
                                        format="ampm"
                                    ></v-time-picker>
                                </v-menu>
                            </v-flex>
                        </v-layout>
                    </v-flex>
                    <v-flex xs12 sm6 class="form-data">
                        <v-layout row wrap>
                            <v-flex xs12 sm6 :class="isMobile?'':'pr-10'">
                                <v-menu v-model="menuDisplayEndDateDay" :close-on-content-click="false" :nudge-right="40"
                                    lazy transition="scale-transition" offset-y full-width min-width="290px">
                                    <template v-slot:activator="{ on }">
                                        <v-text-field v-model="displayEndDateDay" label="Display End Date" readonly v-on="on" :rules="[rules.required]" :disabled="disabled||!displayStartDateDay" hint="When the announcement becomes hidden"></v-text-field>
                                    </template>
                                    <v-date-picker no-title v-model="displayEndDateDay" :allowed-dates="allowedDatesForDisplayEndDate" @input="menuDisplayEndDateDay = false"></v-date-picker>
                                </v-menu>
                            </v-flex>
                            <v-flex xs12 sm6 :class="isMobile?'':'pl-10'">
                                <v-menu
                                    ref="menuDisplayEndDateTime"
                                    v-model="menuDisplayEndDateTime"
                                    :close-on-content-click="false"
                                    :return-value.sync="displayEndDateTime"
                                    lazy
                                    transition="scale-transition"
                                    offset-y
                                >
                                    <template v-slot:activator="{ on }">
                                    <v-text-field
                                        label="Display End Time"
                                        v-model="displayEndDateTime"
                                        :disabled="disabled || !displayEndDateDay"
                                        readonly
                                        v-on="on"
                                        :rules="[rules.required]"
                                    ></v-text-field>
                                    </template>
                                    <v-time-picker
                                        v-if="menuDisplayEndDateTime"
                                        v-model="displayEndDateTime"
                                        :allowed-hours="allowedDisplayEndHours"
                                        :allowed-minutes="allowedDisplayEndMinutes"
                                        full-width
                                        @click:minute="$refs.menuDisplayEndDateTime.save(displayEndDateTime)"
                                        scrollable
                                        format="ampm"
                                    ></v-time-picker>
                                </v-menu>
                            </v-flex>
                        </v-layout>
                    </v-flex>
                </v-layout>
                
                <!-- details link, display duration -->
                <v-layout row wrap>
                    <v-flex xs12 sm6 class="form-data">
                        <v-text-field label="External Link (optional)"
                            v-model="detailsLink"
                            :disabled="disabled"
                            hint="Add an external URL to make the header clickable"
                            ></v-text-field>
                    </v-flex>
                    <v-flex xs12 sm6 class="form-data mb-15">
                         <v-switch
                            v-model="displayDuration"
                            label="Show Dates in Banner"
                            color="primary"
                            hide-details
                        ></v-switch>
                        <span class="caption grey--text" v-if="displayDuration">Start and end dates are shown on the banner</span>
                        <span class="caption grey--text" v-else>Dates are hidden from the banner</span>
                    </v-flex>
                </v-layout>

                <!-- details -->
                <v-layout row wrap>
                    <v-flex xs12 sm12 class="form-data">
                        <v-textarea
                            v-model="details"
                            :rules="[rules.required]"
                            label="Announcement Details"
                            rows="2"
                            color="primary"></v-textarea>
                    </v-flex>
                </v-layout>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="mr-20">
                <v-spacer></v-spacer>
                <v-btn v-if="mode=='Create'" title="Reset Form" @click="resetForm">Reset</v-btn>
                <v-btn title="Save" type="submit" class="primary" dark :loading="loading">Save</v-btn>
            </v-card-actions>
        </v-form>
    </v-card>
</template>
<script>

import companyAnnouncementsApi from '@/api/companyannouncements'

export default {
    data () {
        return {
            error: false,
            errorMessage: null,
            loading: false,
            disabled: false,
            search: null,
            rules: {
                required: value => !!value || 'Required field',
            },
            mode: '',
            menuEffectiveDateDay: false,
            menuEffectiveDateTime: false,
            menuExpirationDateDay: false,
            menuExpirationDateTime: false,
            menuDisplayStartDateDay: false,
            menuDisplayStartDateTime: false,
            menuDisplayEndDateDay: false,
            menuDisplayEndDateTime: false,
            effectiveDateDay: null,
            effectiveDateTime: null,
            expirationDateDay: null,
            expirationDateTime: null,
            displayStartDateDay: null,
            displayStartDateTime: null,
            displayEndDateDay: null,
            displayEndDateTime: null,
            displayDuration: false,
            categories: [],
            companyAnnouncementId: null,
            categoryId: null,
            categoryTypeId: null,
            title: null,
            details: null,
            detailsLink: null
        }
    },
    methods: {      
        allowedDatesForEffectiveDate(val) {
            return this.$moment(val).format('YYYY-MM-DD') >= this.$moment(new Date()).format('YYYY-MM-DD')
        },
        allowedDatesForExpirationDate(val) {
            return (
                this.$moment(val).format('YYYY-MM-DD') >=
                this.$moment(this.effectiveDateDay).format('YYYY-MM-DD')
            )
        },
        allowedDatesForDisplayStartDate(val) {
            const date = this.$moment(val).format('YYYY-MM-DD')
            const today = this.$moment().format('YYYY-MM-DD')
            const expiration = this.$moment(this.expirationDateDay).format('YYYY-MM-DD')

            return date >= today && date <= expiration
        }, 
        allowedDatesForDisplayEndDate(val) {
            return (
                this.$moment(val).format('YYYY-MM-DD') >=
                    this.$moment(new Date()).format('YYYY-MM-DD') &&

                this.$moment(val).format('YYYY-MM-DD') >=
                    this.$moment(this.displayStartDateDay).format('YYYY-MM-DD') &&

                this.$moment(val).format('YYYY-MM-DD') <=
                    this.$moment(this.expirationDateDay).format('YYYY-MM-DD')
            )
        },
        convertTo24Hour(time) {
            return this.$moment(time, ['h:mm A']).format('HH:mm')
        },
        allowedExpirationHours(hour) {
            // if different dates, allow all
            if (this.expirationDateDay !== this.effectiveDateDay) {
                return true
            }
            if (!this.effectiveDateTime) {
                return true
            }
            const effectiveHour = parseInt(
                this.convertTo24Hour(this.effectiveDateTime).split(':')[0]
            )
            return hour >= effectiveHour
        },
        allowedExpirationMinutes(minute) {
            // if different dates, allow all
            if (this.expirationDateDay !== this.effectiveDateDay) {
                return true
            }
            if (!this.effectiveDateTime || !this.expirationDateTime) {
                return true
            }
            const [effectiveHour, effectiveMinute] =
                this.convertTo24Hour(this.effectiveDateTime)
                    .split(':')
                    .map(Number)

            const selectedHour = parseInt(
                this.convertTo24Hour(this.expirationDateTime).split(':')[0]
            )

            // only restrict minutes when same hour
            if (selectedHour === effectiveHour) {
                return minute >= effectiveMinute
            }

            return true
        },
        allowedDisplayEndHours(hour) {
            if (this.displayEndDateDay !== this.displayStartDateDay) {
                return true
            }
            if (!this.displayStartDateTime) {
                return true
            }
            const startHour = parseInt(
                this.convertTo24Hour(this.displayStartDateTime).split(':')[0]
            )
            return hour >= startHour
        },
        allowedDisplayEndMinutes(minute) {
            if (this.displayEndDateDay !== this.displayStartDateDay) {
                return true
            }
            if (!this.displayStartDateTime || !this.displayEndDateTime) {
                return true
            }
            const [startHour, startMinute] =
                this.convertTo24Hour(this.displayStartDateTime)
                    .split(':')
                    .map(Number)

            const selectedHour = parseInt(
                this.convertTo24Hour(this.displayEndDateTime).split(':')[0]
            )
            if (selectedHour === startHour) {
                return minute >= startMinute
            }
            return true
        },
        onEffectiveDateInput() {
            this.menuEffectiveDateDay = false
            if (this.expirationDateDay &&
                this.$moment(this.expirationDateDay).format('YYYY-MM-DD') < 
                this.$moment(this.effectiveDateDay).format('YYYY-MM-DD')
            ) {
                this.expirationDateDay = null
                this.expirationDateTime = null
            }

            if (
                this.displayStartDateDay &&
                this.$moment(this.displayStartDateDay).format('YYYY-MM-DD') <
                this.$moment(this.effectiveDateDay).format('YYYY-MM-DD')
            ) {
                this.displayStartDateDay = null
                this.displayStartDateTime = null
            }

            if (
                this.displayEndDateDay &&
                this.$moment(this.displayEndDateDay).format('YYYY-MM-DD') <
                this.$moment(this.effectiveDateDay).format('YYYY-MM-DD')
            ) {
                this.displayEndDateDay = null
                this.displayEndDateTime = null
            }
        },
        getChipColor() {
            const category = this.categories.find(
                x => x.categoryId == this.categoryId
            )
            return category ? category.backgroundColor : 'transparent'
        },
        async getCategories() {
            try {
                this.loading = true
                const response = await companyAnnouncementsApi.getCategories()
                if (response && response.status == 200) {
                    this.categories = response.data.items
                }
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async saveCompanyAnnouncement() {
            if (this.$refs.form.validate()) {
                try {
                    this.loading = true
                    this.disabled = true

                    const effectiveDateAndTime =
                        this.$moment(this.effectiveDateDay).format('YYYY-MM-DD') + ' ' +
                        this.effectiveDateTime

                    const expirationDateAndTime =
                        this.$moment(this.expirationDateDay).format('YYYY-MM-DD') + ' ' +
                        this.expirationDateTime

                    const displayStartDateAndTime =
                        this.$moment(this.displayStartDateDay).format('YYYY-MM-DD') + ' ' +
                        this.displayStartDateTime

                    const displayEndDateAndTime =
                        this.$moment(this.displayEndDateDay).format('YYYY-MM-DD') + ' ' +
                        this.displayEndDateTime

                    const data = {
                        companyAnnouncementId: this.companyAnnouncementId,
                        categoryId: this.categoryId,
                        categoryTypeId: this.categoryTypeId,
                        effectiveDate: effectiveDateAndTime,
                        expirationDate: expirationDateAndTime,
                        displayStartDate: displayStartDateAndTime,
                        displayEndDate: displayEndDateAndTime,
                        displayDuration: this.displayDuration,
                        title: this.title,
                        details: this.details,
                        detailsLink: this.detailsLink
                    }

                    const response = await companyAnnouncementsApi.upsert(data)
                    if (response && response.status == 200) {
                        this.$emit('send', null)
                        this.closeDialog()
                    }
                    this.loading = false
                    this.disabled = false
                } catch(error) {
                    this.errorMessage = error
                    this.error = true
                }
            }
        },
        async setAdd() {
            this.mode = 'Create'
            
            if (!this.categories.length) {
                await this.getCategories()
            }
            
            this.companyAnnouncementId = null
            this.$refs.form.reset()
        },
        async setEdit(data) {
            this.mode = 'Edit'

            if (!this.categories.length) {
                await this.getCategories()
            }
            
            this.companyAnnouncementId = data.companyAnnouncementId
            this.categoryId = data.categoryId
            this.categoryTypeId = data.categoryTypeId
            this.title = data.title
            this.details = data.details
            this.detailsLink = data.detailsLink
            this.displayDuration = data.displayDuration
           
            this.effectiveDateDay = this.$moment(data.effectiveDate).format('YYYY-MM-DD')
            this.effectiveDateTime = this.$moment(data.effectiveDate).format('HH:mm')
            this.expirationDateDay = this.$moment(data.expirationDate).format('YYYY-MM-DD')
            this.expirationDateTime = this.$moment(data.expirationDate).format('HH:mm')

            this.displayStartDateDay = this.$moment(data.displayStartDate).format('YYYY-MM-DD')
            this.displayStartDateTime = this.$moment(data.displayStartDate).format('HH:mm')
            this.displayEndDateDay = this.$moment(data.displayEndDate).format('YYYY-MM-DD')
            this.displayEndDateTime = this.$moment(data.displayEndDate).format('HH:mm')
        },
        closeDialog() {
            this.$emit('update:dialogAddEdit', false)
        },
        resetForm() {
            this.$refs.form.reset()
        },
    },
    computed: {
        categoryTypes() {
            const category = this.categories.find(
                x => x.categoryId == this.categoryId
            )
            return category ? category.types : []
        },
    }
}
</script>
<style scoped>
.form-data {
    padding: 0px 20px 0px 20px;
}
</style>